// L'AVANZAMENTO di QBridge sul DISPOSITIVO (K9a, condizione 9 di tma-orcr): la prima mano giocata, le lezioni finite e la scelta dei quattro
// colori. UN file solo tocca `localStorage` in tutta la sezione, con un ELENCO ESATTO di chiavi `qlash.bridge.*` (lo tiene fermo
// `tools/verify-bridge.ts`, come `impostazioni.ts` del Burraco): rinominarle azzererebbe l'avanzamento di chi l'ha fatto.
// Niente `sessionStorage`, `indexedDB` o cookie, e niente va al server: sono dati di questo telefono. UNICA eccezione, e NON di questo file:
// il passaggio di `primaManoFatta` da falso a vero lo guarda `lib/telemetria/primaManoBridge.ts` da FUORI da K9a (`useProgressi`) e manda un solo
// segnale anonimo (la lingua dell'interfaccia, niente identita') al game host che conta le prime mani (ADR-0236). Questo file non lo sa e non lo chiama.
//
// Lo schema e' quello di `lib/burraco/impostazioni.ts`: `localStorage` puo' LANCIARE in una WebView con i dati di sito bloccati, non solo
// tornare vuoto, quindi ogni accesso (anche solo toccare `window.localStorage`) e' in `try/catch`, e uno storage che manca o lancia vuol
// dire «nessun avanzamento ricordato»: mai un errore a schermo. Dentro la visita l'avanzamento sta in memoria, cosi' la schermata e'
// coerente con quello che la persona ha appena fatto anche se lo storage non salva; alla visita dopo non c'e' niente da ritrovare.
import { useCallback, useSyncExternalStore } from 'react';

export const CHIAVE_PRIMA_MANO_FATTA = 'qlash.bridge.primaManoFatta';
export const CHIAVE_LEZIONI_FATTE = 'qlash.bridge.lezioniFatte';
export const CHIAVE_QUATTRO_COLORI = 'qlash.bridge.quattroColori';
/** L'elenco ESATTO delle chiavi che la sezione scrive. `verify-bridge` lo confronta con il sorgente. */
export const CHIAVI_BRIDGE = [CHIAVE_PRIMA_MANO_FATTA, CHIAVE_LEZIONI_FATTE, CHIAVE_QUATTRO_COLORI] as const;

export interface Progressi {
  primaManoFatta: boolean;
  /** Gli id delle lezioni finite (`L01`…), senza doppioni, nell'ordine in cui sono state finite. */
  lezioniFatte: readonly string[];
  quattroColori: boolean;
}

export const PROGRESSI_VUOTI: Readonly<Progressi> = { primaManoFatta: false, lezioniFatte: [], quattroColori: false };

type Lettura = Pick<Storage, 'getItem'> | null;
type Scrittura = Pick<Storage, 'setItem'> | null;

/** Solo «true» e «false» sono valori: qualunque altra cosa (assente, vuoto, «1», un testo qualunque) vale il default. */
function leggiBooleano(storage: Lettura, chiave: string): boolean {
  try {
    return storage?.getItem(chiave) === 'true';
  } catch {
    return false;
  }
}

const ID_LEZIONE = /^L(0[1-9]|1[0-3])$/;

function leggiLezioni(storage: Lettura): string[] {
  try {
    const raw = storage?.getItem(CHIAVE_LEZIONI_FATTE);
    if (!raw) return [];
    const x: unknown = JSON.parse(raw);
    if (!Array.isArray(x)) return [];
    return [...new Set(x.filter((v): v is string => typeof v === 'string' && ID_LEZIONE.test(v)))];
  } catch {
    return [];
  }
}

function scrivi(storage: Scrittura, chiave: string, valore: string): void {
  try {
    storage?.setItem(chiave, valore);
  } catch {
    /* un avanzamento che non si salva non e' un errore da mostrare: vale per questa visita */
  }
}

export function leggiProgressi(storage: Lettura): Progressi {
  return {
    primaManoFatta: leggiBooleano(storage, CHIAVE_PRIMA_MANO_FATTA),
    lezioniFatte: leggiLezioni(storage),
    quattroColori: leggiBooleano(storage, CHIAVE_QUATTRO_COLORI),
  };
}

export const scriviPrimaManoFatta = (storage: Scrittura): void => scrivi(storage, CHIAVE_PRIMA_MANO_FATTA, 'true');
export const scriviQuattroColori = (storage: Scrittura, valore: boolean): void => scrivi(storage, CHIAVE_QUATTRO_COLORI, String(valore));
export const scriviLezioniFatte = (storage: Scrittura, ids: readonly string[]): void => scrivi(storage, CHIAVE_LEZIONI_FATTE, JSON.stringify(ids));

/** Lo storage del dispositivo, o `null`: anche solo toccare `window.localStorage` puo' lanciare (cookie e dati di sito bloccati). */
export function storageDelDispositivo(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

// ───────────────────────── lo store in memoria, per React ─────────────────────────
// La schermata d'ingresso e la lezione devono vedere lo STESSO avanzamento nello stesso istante, e `localStorage` da solo non avvisa nessuno.
let memoria: Progressi | null = null;
const ascoltatori = new Set<() => void>();

const corrente = (): Progressi => (memoria ??= leggiProgressi(storageDelDispositivo()));
const iscrivi = (f: () => void) => {
  ascoltatori.add(f);
  return () => {
    ascoltatori.delete(f);
  };
};
function imposta(prossimo: Progressi): void {
  memoria = prossimo;
  ascoltatori.forEach((f) => f());
}

/** Solo per i test: dimentica la memoria, cosi' la lettura successiva riparte dallo storage. */
export function azzeraMemoriaProgressi(): void {
  memoria = null;
  ascoltatori.forEach((f) => f());
}

export function useProgressi() {
  const progressi = useSyncExternalStore(iscrivi, corrente, () => PROGRESSI_VUOTI as Progressi);
  const segnaPrimaMano = useCallback(() => {
    if (corrente().primaManoFatta) return;
    scriviPrimaManoFatta(storageDelDispositivo());
    imposta({ ...corrente(), primaManoFatta: true });
  }, []);
  const segnaLezione = useCallback((id: string) => {
    const gia = corrente().lezioniFatte;
    if (gia.includes(id) || !ID_LEZIONE.test(id)) return;
    const prossime = [...gia, id];
    scriviLezioniFatte(storageDelDispositivo(), prossime);
    imposta({ ...corrente(), lezioniFatte: prossime });
  }, []);
  const impostaQuattroColori = useCallback((valore: boolean) => {
    scriviQuattroColori(storageDelDispositivo(), valore);
    imposta({ ...corrente(), quattroColori: valore });
  }, []);
  return { progressi, segnaPrimaMano, segnaLezione, impostaQuattroColori };
}
